<script setup>
// Racine de l'app : splash pendant le bootstrap (tentative de refresh
// silencieux), ToastContainer global, sinon RouterView.
//
// Après le bootstrap, on force une re-navigation vers la route courante
// pour que le guard beforeEach réévalue les règles (ex: SUPERADMIN → /superadmin).
import { onMounted } from 'vue';
import { RouterView, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth';
import { useAppVersion } from '@/composables/useAppVersion';
import ToastContainer from '@/components/ui/ToastContainer.vue';
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue';
import BaseSpinner from '@/components/ui/BaseSpinner.vue';
import AssistantWidget from '@/components/assistant/AssistantWidget.vue';

const auth = useAuthStore();
const router = useRouter();
const { bootstrapping } = storeToRefs(auth);
const { t } = useI18n();

// Nouvelle version déployée → mise à jour OBLIGATOIRE (modal bloquant, pas de « plus tard »).
const { updateAvailable, latestVersion, reload } = useAppVersion();

onMounted(async () => {
  await auth.bootstrap();
  const current = router.currentRoute.value;

  // Routes publiques (magic links, etc.) : on ne touche à rien, même connecté
  if (current.meta.public) return;

  if (!auth.isAuthenticated) {
    if (current.meta.requiresAuth) {
      router.replace({ name: 'login', query: { next: current.fullPath } });
    }
    return;
  }

  if (auth.isSuperadmin) {
    // SUPERADMIN → toujours vers le backoffice sauf s'il est déjà dessus
    if (!current.meta.requiresSuperadmin) {
      router.replace({ name: 'superadmin' });
    }
    return;
  }

  // RESELLER (hors impersonation) → son espace, sauf s'il y est déjà.
  if (auth.isReseller && !auth.isImpersonating) {
    if (!current.meta.requiresReseller) {
      router.replace({ name: 'reseller' });
    }
    return;
  }

  // Utilisateur tenant → re-naviguer pour déclencher les guards (features, etc.)
  router.replace(current.fullPath).catch(() => null);
});
</script>

<template>
  <div>
    <div
      v-if="bootstrapping"
      class="min-h-screen flex items-center justify-center bg-gray-50"
    >
      <BaseSpinner size="lg" />
    </div>
    <RouterView v-else />
    <ToastContainer />
    <ConfirmDialog />
    <AssistantWidget v-if="!bootstrapping" />

    <!-- Nouvelle version déployée : mise à jour OBLIGATOIRE (bloquant, hard refresh) -->
    <div
      v-if="updateAvailable"
      class="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
    >
      <div class="w-full max-w-sm rounded-xl bg-white p-6 text-center shadow-2xl">
        <div class="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-2xl">
          🔄
        </div>
        <h2 class="text-lg font-semibold text-gray-900">
          {{ t('app.updateTitle') }}
        </h2>
        <p class="mt-2 text-sm text-gray-600">
          {{ t('app.updateAvailable', { version: latestVersion }) }}
        </p>
        <button
          type="button"
          class="mt-5 w-full rounded-md bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700"
          @click="reload"
        >
          {{ t('app.updateNow') }}
        </button>
      </div>
    </div>
  </div>
</template>
